import React, { useRef } from "react";
import { NavLink, Link } from "react-router-dom";
import { Grid } from "@mui/material";
import { DateTimeStandard, Button, Chip, MenuItem, SelectField, Stack, TextField, Typography } from "@curves/ui-base";
import { PageLayout, MemberSecureCard, OverflowMenu } from "@icoach/components";
import { DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER } from "@icoach/router/routerPath";
import { formatCurrencyFn, refIsRequiredError, parseCardExpiry, isEmpty } from "@util/utils";
import useOverview from "@util/hook/useOverview";
import useOptions from "@util/hook/useOptions";
import { parseDate } from "@util/moment";
import useCyclePurchaseApi from "@apis/useCyclePurchaseApi";
import usePrivacyToggle, { securityMaskFilterTypeMap } from "@util/hook/usePrivacyToggle";
import { OverviewListLayout, OverviewListActionBar, PageSortAndTotalBox, OverviewListToolbar, OverviewListSingleActions } from "@icoach/components/overviewList";
import { getInitialPagination, COLOR_MATCH, filterMenu, getHealthyCyclePurchaseMenuMap } from "@icoach/healthyCyclePurchase/CyclePurchase.util";
import CyclePurchaseDialogBox from "@icoach/healthyCyclePurchase/dialog/CyclePurchaseDialogBox";
import { CycleOrderListBox } from "@icoach/healthyCyclePurchase/components";
import { CYCLEORDER_TYPE } from "@constants/";

// 表頭欄位定義
const headerRow = [
	{
		cellKey: "cycleOrderNo",
		headerLabel: "循環訂單編號",
		width: "12",
		align: "center",
		formatCell: (cellValue, { memberID, cycleOrderID, statusColor, cycleOrderTypeText }) => (
			<Stack direction={"column"}>
				<Button component={NavLink} to={DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.DETAIL.GET_MAIN_PATH(memberID, cycleOrderID)}>
					{cellValue}
				</Button>
				<Chip label={cycleOrderTypeText} color={COLOR_MATCH[statusColor]} title={"訂單狀態"} />
			</Stack>
		),
	},
	{
		cellKey: "displayName",
		headerLabel: "會員",
		formatCell: (displayName, { maskDisplayName, memberImg, memberNo, nickName }, { isRevealed }) => {
			return <MemberSecureCard name={displayName} maskName={maskDisplayName} memberNo={memberNo} nickname={nickName} avatar={memberImg} isRevealed={isRevealed} />;
		},
	},
	{
		cellKey: "items",
		headerLabel: "訂單內容",
		formatCell: (value, { shippingDayText }) => {
			return <CycleOrderListBox orderData={value} shippingDayText={shippingDayText} />;
		},
	},
	{
		cellKey: "saleAmount",
		headerLabel: "訂單金額",
		align: "center",
		formatCell: cellValue => formatCurrencyFn(cellValue),
	},
	{
		cellKey: "cardExpiryDate",
		headerLabel: "信用卡效期",
		align: "center",
		formatCell: cellValue => {
			return parseCardExpiry(cellValue);
		},
	},
	{
		cellKey: "updatedDate",
		headerLabel: "更新日期",
		width: "12",
		formatCell: cellValue => {
			return (
				<React.Fragment>
					<Typography className="mb-0">{parseDate(cellValue, DateTimeStandard.DisplayDateBySlash)}</Typography>
					<Typography className="mb-0 font-color-3">{parseDate(cellValue, DateTimeStandard.DisplayTime)}</Typography>
				</React.Fragment>
			);
		},
	},
	{
		cellKey: "employeeName",
		headerLabel: "擔當",
		align: "center",
	},
	{
		cellKey: "cycleOrderID",
		headerLabel: "",
		align: "right",
		isIcon: true,
		formatCell: (_, rowData, { refresh, dialogRef }) => {
			let menuData = filterMenu(
				getHealthyCyclePurchaseMenuMap(
					{
						memberID: rowData?.memberID,
						cycleOrderID: rowData?.cycleOrderID,
						effectID: rowData?.effectID ?? 0,
						canReview: true,
						canModify: rowData?.canModify,
						canSuspend: rowData?.canSuspend,
						canCancel: rowData?.canCancel,
						canResumeNextCycle: rowData?.canResumeNextCycle,
						canDeleted: rowData?.canDeleted,
						canEnableFirstOrder: rowData?.canEnableFirstOrder,
					},
					{
						dialogRef: dialogRef,
						refresh: refresh,
					},
				),
			);
			return <OverflowMenu menuData={menuData} enabled={!isEmpty(menuData)} />;
		},
	},
];

// 頁面操作按鈕
const pageActions = [
	{
		label: "新增循環訂購單",
		variant: "contained",
		to: DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.CREATE,
		as: Link,
	},
];

// 排序預設值
const defaultSortBy = 2;

const CyclePurchaseOverviewPage = () => {
	const { postCyclePurchaseOverviewApi, getCyclePurchaseOverviewOptionsApi } = useCyclePurchaseApi();

	// 使用 options hook 取得選項資料
	const { options } = useOptions({ getOptionsApi: getCyclePurchaseOverviewOptionsApi, isFetchOnMount: true });
	const { shippingDayOptions = [], cycleOrderTypeOptions = [], coachOptions: employeeOptions = [], cycleOrderOrderByOptions } = options || {};

	// 使用 overview hook 管理總覽資料
	const { overviewData, searchParams, getOverviewData, updateOverviewPage, updateOverviewSort } = useOverview({
		getOverviewApi: postCyclePurchaseOverviewApi,
		defaultSearchParams: {
			...getInitialPagination(defaultSortBy),
			memberSearch: "",
			shippingDay: 99,
			cycleOrderType: 1,
			cycleOrderNo: "",
			productName: "",
			employeeID: 0,
		},
		enableInfiniteScroll: true,
		isFetchOnMount: false,
	});

	// 使用隱私切換 hook
	const { isRevealed, toggle, renderPrivacyIcon } = usePrivacyToggle();

	// refs
	const memberSearchRef = useRef(null);
	const shippingDayRef = useRef(null);
	const cycleOrderTypeRef = useRef(null);
	const cycleOrderNoRef = useRef(null);
	const productNameRef = useRef(null);
	const employeeRef = useRef(null);

	// 對話框 ref
	const dialogRef = useRef(null);

	// 是否顯示工具列
	const showToolbar = overviewData?.list?.length;

	const getParamsData = () => {
		let result = { ...getInitialPagination(defaultSortBy) };

		if (memberSearchRef.current && memberSearchRef.current.getResult) {
			let value = memberSearchRef.current.getResult();
			result.memberSearch = value.trim();
		}

		if (shippingDayRef.current && shippingDayRef.current.getResult) {
			let value = shippingDayRef.current.getResult();
			value = value.trim();
			result.shippingDay = value === "" ? 99 : value;
		}

		if (cycleOrderTypeRef.current && cycleOrderTypeRef.current.getResult) {
			let value = cycleOrderTypeRef.current.getResult();
			value = value.trim();
			result.cycleOrderType = value === "" ? CYCLEORDER_TYPE.CREDITCARDVALIDORDER : value;
		}

		if (cycleOrderNoRef.current && cycleOrderNoRef.current.getResult) {
			let value = cycleOrderNoRef.current.getResult();
			result.cycleOrderNo = value.trim();
		}

		if (productNameRef.current && productNameRef.current.getResult) {
			let value = productNameRef.current.getResult();
			value = value.trim();
			result.productName = value.trim();
		}

		if (employeeRef.current && employeeRef.current.getResult) {
			let value = employeeRef.current.getResult();
			value = value.trim();
			result.employeeID = value === "" ? 0 : value;
		}

		return result;
	};

	const handleSubmit = () => {
		const paramsData = getParamsData();
		const isError = refIsRequiredError(memberSearchRef, shippingDayRef, cycleOrderTypeRef, cycleOrderNoRef, productNameRef, employeeRef);

		if (!isError) {
			getOverviewData(paramsData);
		}
	};

	const handlePageChange = (e, page) => {
		updateOverviewPage(page);
	};

	const handleSortChange = value => {
		updateOverviewSort(value);
	};

	const handleToggleRevealed = () => {
		const apiParams = {
			filterType: securityMaskFilterTypeMap.ClubMemberCyclePurchaseOverview,
			totalCount: overviewData?.list?.length,
		};
		toggle(apiParams);
	};

	return (
		<PageLayout>
			<PageLayout.Header title="循環訂單總覽" actions={pageActions} />
			<OverviewListLayout>
				<OverviewListLayout.SearchBar className={!showToolbar && "mb-3"} onSubmit={handleSubmit} isShowActions>
					<Grid spacing={2} container>
						<Grid xs={4} item>
							<TextField
								label="會員"
								key={searchParams?.memberSearch}
								ref={memberSearchRef}
								defaultValue={searchParams?.memberSearch}
								InputProps={{ placeholder: "請輸入會員姓名或會員編號或手機" }}
								fullWidth
							/>
						</Grid>
						<Grid xs={4} item>
							<SelectField label="出貨週期" key={searchParams?.shippingDay} ref={shippingDayRef} defaultValue={searchParams?.shippingDay} fullWidth>
								{Array.isArray(shippingDayOptions) &&
									shippingDayOptions
										.filter(item => item || !item.disabled)
										.map(item => (
											<MenuItem key={item.value} value={item.value}>
												{item.text}
											</MenuItem>
										))}
							</SelectField>
						</Grid>
						<Grid xs={4} item>
							<SelectField label="訂單狀態" key={searchParams?.cycleOrderType} ref={cycleOrderTypeRef} defaultValue={searchParams?.cycleOrderType} fullWidth>
								{Array.isArray(cycleOrderTypeOptions) &&
									cycleOrderTypeOptions
										.filter(item => item || !item.disabled)
										.map(item => (
											<MenuItem key={item.value} value={item.value}>
												{item.text}
											</MenuItem>
										))}
							</SelectField>
						</Grid>
						<Grid xs={4} item>
							<TextField
								label="訂單編號"
								key={searchParams?.cycleOrderNo}
								ref={cycleOrderNoRef}
								defaultValue={searchParams?.cycleOrderNo}
								InputProps={{ placeholder: "請輸入訂單編號" }}
								fullWidth
							/>
						</Grid>
						<Grid xs={4} item>
							<TextField
								label="商品名稱"
								key={searchParams?.productName}
								ref={productNameRef}
								defaultValue={searchParams?.productName}
								InputProps={{ placeholder: "請輸入商品名稱" }}
								fullWidth
							/>
						</Grid>
						<Grid xs={4} item>
							<SelectField label="擔當" key={searchParams?.employeeID} ref={employeeRef} defaultValue={searchParams?.employeeID} fullWidth displayEmpty>
								{Array.isArray(employeeOptions) &&
									employeeOptions
										.filter(item => item || !item.disabled)
										.map(item => (
											<MenuItem key={item.value} value={item.value}>
												{item.text}
											</MenuItem>
										))}
							</SelectField>
						</Grid>
					</Grid>
				</OverviewListLayout.SearchBar>
				{showToolbar ? (
					<OverviewListToolbar>
						<OverviewListActionBar>
							<OverviewListSingleActions>{renderPrivacyIcon(handleToggleRevealed)}</OverviewListSingleActions>
						</OverviewListActionBar>
						<PageSortAndTotalBox
							pageIndex={overviewData?.pagination?.pageIndex}
							pageSize={overviewData?.pagination?.pageSize}
							totalCount={overviewData?.pagination?.totalCount}
							sortDefaultValue={searchParams?.sortBy || defaultSortBy}
							sortOptions={cycleOrderOrderByOptions}
							sortOnChange={handleSortChange}
							isCurrentPageLastIndex
							isSort
						/>
					</OverviewListToolbar>
				) : null}
				<OverviewListLayout.Table
					headerRow={headerRow}
					list={overviewData?.list || []}
					bodyRowClass={{
						key: "enabled",
						value: false,
						className: "overview-item-invalid",
					}}
					totalPage={overviewData?.pagination?.totalPage}
					pageIndex={overviewData?.pagination?.pageIndex}
					onChangePage={handlePageChange}
					refresh={() => getOverviewData(searchParams)}
					tableRowBodyProps={{ isRevealed, dialogRef }}
					isMasonryPagination
				/>
			</OverviewListLayout>
			<CyclePurchaseDialogBox ref={dialogRef} />
		</PageLayout>
	);
};

export default CyclePurchaseOverviewPage;
